/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  CardContainer,
  DepartmentCard,
  DepartmentModal,
  SuccessModal,
  ConfirmationModal,
} from '../index';
import { useState } from 'react';
import { apiUrl } from '../../utilities/apiUrl';

export const ViewAllDepartments = (props) => {
  const departmentList = props.departmentList;
  const entryPerPage = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [members, setMembers] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState('');
  const [deptName, setDeptName] = useState('');
  const [deptDesc, setDeptDesc] = useState('');
  const [deptMandaWorkDesc, setDeptMandaWorkDesc] = useState('');
  const [deptLocation, setDeptLocation] = useState('');
  const deptMembers = [];
  const [employees, setEmployees] = useState([]);
  const [employeeName, setEmployeeName] = useState([]);
  const [deptMessaging, setDeptMessaging] = useState('');
  const [isEdited, setIsEdited] = useState(true);

  // VIEW SPECIFIC DEPARTMENT HANDLER
  const handleViewDepartment = (index) => {
    const currentDept = departmentList[index];

    setSelectedDept(currentDept._id);
    setDeptName(currentDept.deptname);
    setDeptDesc(currentDept.deptdesc);
    setDeptMandaWorkDesc(currentDept.mandaworkdesc);
    setDeptLocation(currentDept.location);
    setDeptMessaging(currentDept.messaging);
    getEmployeeNames();
  };

  // DELETE A DEPARTMENT HANDLER
  const handleDeleteDepartment = () => {
    setViewModalOpen(false);

    fetch(apiUrl('/department/' + selectedDept), {
      method: 'DELETE',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setConfirmModalOpen(false);
          setIsEdited(false);
          setSuccessModalOpen(true);
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // EDIT A DEPARTMENT HANDLER
  const handleSaveEditedDepartment = () => {
    // GET ALL EMPIDS OF THE SELECTED MEMBERS
    employeeName.forEach((member) => {
      employees.forEach((emp) => {
        if (member === emp.name) {
          deptMembers.push(emp.id);
        }
      });
    });

    fetch(apiUrl('/department/' + selectedDept), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        deptname: deptName,
        location: deptLocation,
        deptdesc: deptDesc,
        mandaworkdesc: deptMandaWorkDesc,
        messaging: deptMessaging,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setViewModalOpen(false);
          setIsEdited(true);
          deptMembers.length !== 0
            ? handleEditMember(deptMembers, { deptid: selectedDept })
            : setSuccessModalOpen(true);
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // ADD OR DELETE MEMBER/S
  const handleEditMember = (empids, newData) => {
    fetch(apiUrl('/employee'), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empids: empids,
        newData: newData,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setSuccessModalOpen(true);
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const resetFields = () => {
    setSelectedDept('');
    setDeptName('');
    setDeptDesc('');
    setDeptMandaWorkDesc('');
    setDeptLocation('');
    setDeptMessaging('');
    setEmployeeName([]);
  };

  // PAGE CHANGE HANDLER
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;
    page === 1
      ? setSlice([0, entryPerPage])
      : setSlice([currPage * entryPerPage, page * entryPerPage]);
  };

  // SELECTED MEMBERS
  const getEmployeeNames = () => {
    const membersList = props.employeeList.map((emp) => ({
      id: emp._id,
      name: `${emp.firstname} ${emp.lastname}`,
    }));

    setEmployees(membersList);
  };

  // SELECTING MULTIPLE DEPARTMENT EMPLOYEES
  const handleSelectMembersChange = (event) => {
    const {
      target: { value },
    } = event;

    const member = typeof value === 'string' ? value.split(',') : value;
    setEmployeeName(member);
  };

  return (
    <>
      <CardContainer
        length={departmentList.length}
        entryPerPage={entryPerPage}
        pageCount={Math.floor(departmentList.length / entryPerPage)}
        handlePageChange={handlePageChange}
        spacing={3}
      >
        {departmentList.slice(slice[0], slice[1]).map((proj, index) => (
          <DepartmentCard
            key={index}
            title={proj.deptname}
            desc={proj.deptdesc}
            members={proj.members}
            onClick={() => {
              setViewModalOpen(true);
              setMembers(proj.members);
              handleViewDepartment(
                (index + entryPerPage) * (currentPage - 1) -
                  (currentPage - 2) * index
              );
            }}
          />
        ))}
      </CardContainer>
      <DepartmentModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setEmployeeName([]);
        }}
        members={members}
        handleConfirmDelete={() => setConfirmModalOpen(true)}
        primaryClickHandler={handleSaveEditedDepartment}
        secondaryClickHandler={() => {
          setViewModalOpen(false);
          setEmployeeName([]);
        }}
        employeeName={employeeName}
        employees={employees}
        handleSelectMembersChange={handleSelectMembersChange}
        deptname={deptName}
        deptdesc={deptDesc}
        mandaworkdesc={deptMandaWorkDesc}
        location={deptLocation}
        messaging={deptMessaging}
        deptNameOnChange={(e) => setDeptName(e.target.value)}
        deptDescOnChange={(e) => setDeptDesc(e.target.value)}
        mandaWorkDescOnChange={(e) => setDeptMandaWorkDesc(e.target.value)}
        locationOnChange={(e) => setDeptLocation(e.target.value)}
        messagingOnChange={(e) => setDeptMessaging(e.target.value)}
      ></DepartmentModal>
      <SuccessModal
        open={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          resetFields();
        }}
        close={() => {
          setSuccessModalOpen(false);
          resetFields();
        }}
        desc={`You have successfully ${
          isEdited ? 'edited' : 'deleted'
        } ${deptName} Department.`}
      ></SuccessModal>
      <ConfirmationModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title="Delete Department"
        desc={`Are you sure you want to delete ${deptName}?`}
        primaryClickHandler={handleDeleteDepartment}
        secondaryClickHandler={() => setConfirmModalOpen(false)}
        primaryBtnName="Delete"
        secondaryBtnName="Cancel"
        primaryStyle={{
          bgcolor: '#d5615d',
          border: '1px solid #d5615d',
          '&:hover': {
            bgcolor: 'rgb(173, 59, 55) !important',
            borderColor: 'rgb(173, 59, 55)',
          },
        }}
      ></ConfirmationModal>
    </>
  );
};
